import { Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { routes } from "wasp/client/router";
import { motion, useReducedMotion } from "framer-motion";
import { trackLandingCta } from "../../client/utils/posthog";
import { useLanguageContext } from "../../client/contexts/LanguageContext";

/**
 * PricingTable : detailed Free / Pro / Max comparison table.
 *
 * Rendered under the pricing cards (landing page + dedicated pricing page).
 * Full bleed dark section locked to the marketing palette (#0F1729) for visual
 * continuity with HowItWorks / FeatureTabs: aurora bloom + terminal dot grid
 * behind the table, and a soft halo that lifts the highlighted Pro column.
 *
 * Copy is bilingual inline ({fr,en}) via useLanguageContext, matching the
 * pricing section's comparison-table convention rather than the global
 * translations.ts dictionary (the dataset is large, structured and table-local).
 *
 * Motion is orchestrated with framer-motion (header + table reveal on scroll)
 * with a full prefers-reduced-motion path that ships the section static.
 */

const TEAL = "#00C9A7";
const AMBER = "#EF9F27";
// Single, consistent teal edge for the highlighted Pro column (header → body →
// footer). A crisp medium-opacity line, not a harsh full-bright border.
const PRO_BORDER = "rgba(0,201,167,0.45)";
const EASE = [0.16, 1, 0.3, 1] as const; // ease-out-expo
const VIEWPORT = { once: true, amount: 0.2 } as const;

type Bilingual = { fr: string; en: string };

// A cell is either a boolean (check / cross), a raw value (number, ∞, "1 compte"),
// or a special badge ("limited" / "soon").
type Cell = boolean | "limited" | "soon" | Bilingual | string;

interface FeatureRow {
  label: Bilingual;
  desc: Bilingual;
  free: Cell;
  pro: Cell;
  max: Cell;
  // Optional status pill shown next to the label (e.g. "Soon", "Beta").
  badge?: { text: Bilingual; tone: "soon" | "beta" };
}

interface CategoryBlock {
  category: Bilingual;
  rows: FeatureRow[];
}

const DATA: CategoryBlock[] = [
  {
    category: { fr: "Comptes & prop firms", en: "Accounts & prop firms" },
    rows: [
      {
        label: { fr: "Comptes de trading", en: "Trading accounts" },
        desc: {
          fr: "Nombre de comptes prop firm connectables simultanément",
          en: "Prop firm accounts you can connect at the same time",
        },
        free: "1",
        pro: "∞",
        max: "∞",
      },
      {
        label: { fr: "Prop Firm Manager", en: "Prop Firm Manager" },
        desc: {
          fr: "Gestion des règles, drawdowns et seuils par compte prop firm",
          en: "Rules, drawdowns and thresholds management per prop firm account",
        },
        free: "limited",
        pro: true,
        max: true,
      },
      {
        label: { fr: "Sync auto Tradovate", en: "Tradovate auto-sync" },
        desc: {
          fr: "Synchronisation automatique des trades via Tradovate (lecture seule)",
          en: "Automatic trade syncing through Tradovate (read-only)",
        },
        badge: { text: { fr: "Bêta", en: "Beta" }, tone: "beta" },
        free: false,
        pro: false,
        max: true,
      },
      {
        label: { fr: "Sync MetaTrader MT4/MT5", en: "MetaTrader MT4/MT5 sync" },
        desc: {
          fr: "Synchronisation automatique via MetaTrader 4 ou 5",
          en: "Automatic syncing through MetaTrader 4 or 5",
        },
        badge: { text: { fr: "Bientôt", en: "Soon" }, tone: "soon" },
        free: false,
        pro: false,
        max: "soon",
      },
    ],
  },
  {
    category: {
      fr: "Sessions, routines & objectifs",
      en: "Sessions, routines & goals",
    },
    rows: [
      {
        label: { fr: "Sessions de trading", en: "Trading sessions" },
        desc: {
          fr: "Nombre de sessions journalisables par mois",
          en: "Sessions you can journal per month",
        },
        free: "∞",
        pro: "∞",
        max: "∞",
      },
      {
        label: { fr: "Routines & checklists", en: "Routines & checklists" },
        desc: {
          fr: "Créer des routines pré/post session personnalisées",
          en: "Build custom pre and post session routines",
        },
        free: "3",
        pro: "∞",
        max: "∞",
      },
      {
        label: { fr: "Objectifs de trading", en: "Trading goals" },
        desc: {
          fr: "Définir et suivre des objectifs de performance",
          en: "Set and track performance goals",
        },
        free: "3",
        pro: "∞",
        max: "∞",
      },
    ],
  },
  {
    category: { fr: "Dashboard", en: "Dashboard" },
    rows: [
      {
        label: { fr: "Dashboard modulaire", en: "Modular dashboard" },
        desc: {
          fr: "Widgets personnalisables : PnL, equity curve, calendrier éco…",
          en: "Customizable widgets: PnL, equity curve, economic calendar…",
        },
        free: true,
        pro: true,
        max: true,
      },
      {
        label: { fr: "Pages personnalisées", en: "Custom pages" },
        desc: {
          fr: "Créer des pages de dashboard sur mesure comme sur Notion",
          en: "Build tailored dashboard pages, Notion style",
        },
        free: false,
        pro: true,
        max: true,
      },
      {
        label: { fr: "Mode mentor", en: "Mentor mode" },
        desc: {
          fr: "Encadrement d'étudiants et suivi de leurs performances",
          en: "Coach students and track their performance",
        },
        free: false,
        pro: true,
        max: true,
      },
    ],
  },
  {
    category: { fr: "Analytics avancées", en: "Advanced analytics" },
    rows: [
      {
        label: {
          fr: "Analytics de trading avancées",
          en: "Advanced trading analytics",
        },
        desc: {
          fr: "Win rate, profit factor, biais cognitifs, performances par horaire",
          en: "Win rate, profit factor, cognitive biases, performance by time slot",
        },
        free: false,
        pro: true,
        max: true,
      },
    ],
  },
  {
    category: { fr: "Support", en: "Support" },
    rows: [
      {
        label: { fr: "Support communautaire", en: "Community support" },
        desc: {
          fr: "Accès au Discord Edgely et à la communauté de traders",
          en: "Access to the Edgely Discord and trader community",
        },
        free: true,
        pro: true,
        max: true,
      },
      {
        label: { fr: "Support prioritaire", en: "Priority support" },
        desc: {
          fr: "Réponse sous 24h par l'équipe Edgely",
          en: "Reply within 24h from the Edgely team",
        },
        free: false,
        pro: true,
        max: true,
      },
      {
        label: { fr: "Futures fonctionnalités", en: "Future features" },
        desc: {
          fr: "Toutes les nouvelles features incluses automatiquement",
          en: "Every new feature included automatically",
        },
        free: false,
        pro: true,
        max: true,
      },
    ],
  },
];

type Plan = "free" | "pro" | "max";

function ValueCell({
  value,
  plan,
  lang,
}: {
  value: Cell;
  plan: Plan;
  lang: "fr" | "en";
}) {
  if (value === true) {
    const color = plan === "max" ? AMBER : TEAL;
    return (
      <Check
        size={18}
        strokeWidth={2.5}
        className="mx-auto"
        style={{ color }}
        aria-label={lang === "fr" ? "Inclus" : "Included"}
      />
    );
  }

  if (value === false) {
    return (
      <X
        size={18}
        strokeWidth={2}
        className="mx-auto"
        style={{ color: "rgba(255,255,255,0.15)" }}
        aria-label={lang === "fr" ? "Non inclus" : "Not included"}
      />
    );
  }

  if (value === "limited") {
    return (
      <span
        className="inline-block font-medium"
        style={{
          fontSize: "11px",
          padding: "2px 10px",
          borderRadius: "999px",
          backgroundColor: "rgba(255,255,255,0.05)",
          border: "0.5px solid rgba(255,255,255,0.08)",
          color: "rgba(255,255,255,0.35)",
        }}
      >
        {lang === "fr" ? "Limité" : "Limited"}
      </span>
    );
  }

  // "Coming soon" pill — same teal tone as the row's Soon label badge, so a
  // not-yet-shipped feature never reads as available ("1 account", a check…).
  if (value === "soon") {
    return (
      <span
        className="inline-flex items-center font-semibold uppercase tracking-wide"
        style={{
          fontSize: "10px",
          padding: "2px 8px",
          borderRadius: "999px",
          backgroundColor: `${TEAL}1A`,
          border: `1px solid ${TEAL}55`,
          color: TEAL,
        }}
      >
        {lang === "fr" ? "Bientôt" : "Soon"}
      </span>
    );
  }

  // Raw text / number value.
  const text = typeof value === "string" ? value : value[lang];
  const color =
    plan === "pro" ? TEAL : plan === "max" ? AMBER : "rgba(255,255,255,0.45)";
  const weight = plan === "free" ? 500 : 600;

  return (
    <span
      className="font-mono tabular-nums"
      style={{ fontSize: "13px", fontWeight: weight, color }}
    >
      {text}
    </span>
  );
}

export default function PricingTable() {
  const { language: lang } = useLanguageContext();
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;

  const goSignup = (plan: Plan) => {
    trackLandingCta("comparison_table", plan);
    navigate(
      plan === "free"
        ? routes.SignupRoute.to
        : `${routes.SignupRoute.to}?plan=${plan}`,
    );
  };

  // Shared col widths (table-layout: fixed). 44% / 18.67% x3.
  const colGroup = (
    <colgroup>
      <col style={{ width: "44%" }} />
      <col style={{ width: "18.67%" }} />
      <col style={{ width: "18.67%" }} />
      <col style={{ width: "18.67%" }} />
    </colgroup>
  );

  // Plan header / footer cell tint for the Pro column. Kept translucent (not a
  // flat dark-green fill) so the teal halo behind the table reads through and
  // the column glows instead of looking like a dull green rectangle.
  const proCellBg = "rgba(0,201,167,0.06)";

  return (
    <section
      className="relative isolate overflow-hidden mx-[calc(-50vw+50%)] w-screen"
      style={{ backgroundColor: "#0F1729" }}
    >
      {/* ── Atmosphere ──────────────────────────────────────────────────── */}
      {/* Aurora bloom anchored on the highlighted Pro column */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(50% 55% at 62% 30%, rgba(0,201,167,0.10), transparent 70%)," +
            "radial-gradient(45% 50% at 82% 88%, rgba(239,159,39,0.05), transparent 72%)",
        }}
      />
      {/* Faint terminal dot grid, masked to fade at the edges */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(circle at center, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(80% 70% at 50% 35%, black, transparent 92%)",
          WebkitMaskImage:
            "radial-gradient(80% 70% at 50% 35%, black, transparent 92%)",
        }}
      />

      <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <motion.div
          className="mx-auto mb-14 max-w-2xl text-center"
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p
            className="font-semibold uppercase"
            style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              color: TEAL,
              marginBottom: "16px",
            }}
          >
            {lang === "fr" ? "Comparatif" : "Comparison"}
          </p>
          <h2
            className="font-display font-bold text-white"
            style={{
              fontSize: "clamp(28px, 5vw, 38px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.12,
            }}
          >
            {lang === "fr"
              ? "Free · Pro · Max : en détail"
              : "Free · Pro · Max: in detail"}
          </h2>
        </motion.div>

        <motion.div
          className="relative"
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        >
          {/* Pro column halo : sits behind the table, aligned to the Pro band
              (44% + 18.67% .. 81.34%). Hidden on small screens where the table
              scrolls. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 -z-10 hidden rounded-2xl md:block"
            style={{
              left: "62.67%",
              width: "18.67%",
              background:
                "linear-gradient(180deg, rgba(0,201,167,0.10), rgba(0,201,167,0.02))",
              boxShadow: "0 30px 80px -30px rgba(0,201,167,0.5)",
            }}
          />

          <div className="overflow-x-auto">
            <table
              className="w-full border-collapse"
              style={{ tableLayout: "fixed", minWidth: "760px" }}
            >
              {colGroup}

              {/* ── Header ──────────────────────────────────────────────── */}
              <thead>
                <tr>
                  <th className="bg-transparent" />

                  {/* Free */}
                  <th className="px-3 pb-5 pt-4 align-top">
                    <PlanHead
                      plan="free"
                      name="Free"
                      price={lang === "fr" ? "Gratuit" : "Free"}
                      cta={lang === "fr" ? "Démarrer →" : "Get started →"}
                      onClick={() => goSignup("free")}
                    />
                  </th>

                  {/* Pro : highlighted column, rounded top */}
                  <th
                    className="px-3 pb-5 pt-4 align-top"
                    style={{
                      backgroundColor: proCellBg,
                      borderTop: `1.5px solid ${PRO_BORDER}`,
                      borderLeft: `1.5px solid ${PRO_BORDER}`,
                      borderRight: `1.5px solid ${PRO_BORDER}`,
                      borderTopLeftRadius: "14px",
                      borderTopRightRadius: "14px",
                    }}
                  >
                    <PlanHead
                      plan="pro"
                      name="Pro"
                      price="€15"
                      period={lang === "fr" ? "/mois" : "/mo"}
                      badge={lang === "fr" ? "Populaire" : "Popular"}
                      cta={
                        lang === "fr" ? "Démarrer avec Pro →" : "Start with Pro →"
                      }
                      onClick={() => goSignup("pro")}
                    />
                  </th>

                  {/* Max */}
                  <th className="px-3 pb-5 pt-4 align-top">
                    <PlanHead
                      plan="max"
                      name="Max"
                      price="€29"
                      period={lang === "fr" ? "/mois" : "/mo"}
                      cta={lang === "fr" ? "Passer à Max →" : "Upgrade to Max →"}
                      onClick={() => goSignup("max")}
                    />
                  </th>
                </tr>
              </thead>

              {/* ── Body ────────────────────────────────────────────────── */}
              <tbody>
                {DATA.map((block) => (
                  <CategoryRows key={block.category.en} block={block} lang={lang} />
                ))}
              </tbody>

              {/* ── Footer : repeated CTAs + trust note ──────────────────── */}
              <tfoot>
                <tr>
                  <td
                    className="px-4 pt-6 align-middle"
                    style={{ fontSize: "12px", color: "rgba(255,255,255,0.25)" }}
                  >
                    {lang === "fr"
                      ? "Pas de CB pour le plan gratuit · Résiliation en 1 clic"
                      : "No credit card for the free plan · Cancel in 1 click"}
                  </td>
                  <td className="px-3 pt-6 text-center align-middle">
                    <PlanCta
                      plan="free"
                      label={lang === "fr" ? "Démarrer →" : "Get started →"}
                      onClick={() => goSignup("free")}
                    />
                  </td>
                  <td
                    className="px-3 pb-5 pt-6 text-center align-middle"
                    style={{
                      backgroundColor: proCellBg,
                      borderBottom: `1.5px solid ${PRO_BORDER}`,
                      borderLeft: `1.5px solid ${PRO_BORDER}`,
                      borderRight: `1.5px solid ${PRO_BORDER}`,
                      borderBottomLeftRadius: "14px",
                      borderBottomRightRadius: "14px",
                    }}
                  >
                    <PlanCta
                      plan="pro"
                      label={lang === "fr" ? "Démarrer →" : "Get started →"}
                      onClick={() => goSignup("pro")}
                    />
                  </td>
                  <td className="px-3 pt-6 text-center align-middle">
                    <PlanCta
                      plan="max"
                      label={lang === "fr" ? "Passer à Max →" : "Upgrade →"}
                      onClick={() => goSignup("max")}
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Category + its feature rows ──────────────────────────────────────────── */

function CategoryRows({
  block,
  lang,
}: {
  block: CategoryBlock;
  lang: "fr" | "en";
}) {
  return (
    <>
      {/* Category label spanning all columns */}
      <tr>
        <td
          colSpan={4}
          className="font-semibold uppercase"
          style={{
            padding: "10px 0 6px 16px",
            fontSize: "10px",
            letterSpacing: "0.1em",
            color: TEAL,
            backgroundColor: "rgba(0,201,167,0.03)",
            borderTop: "0.5px solid rgba(255,255,255,0.08)",
          }}
        >
          {block.category[lang]}
        </td>
      </tr>

      {block.rows.map((row) => (
        <tr key={row.label.en} className="group">
          {/* Feature name + sub-description */}
          <td
            className="transition-colors group-hover:bg-white/[0.02]"
            style={{
              padding: "14px 16px",
              borderTop: "0.5px solid rgba(255,255,255,0.04)",
            }}
          >
            <div style={{ fontSize: "13px", fontWeight: 500, color: "#fff", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {row.label[lang]}
              {row.badge && (
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    padding: "1px 7px",
                    borderRadius: "9999px",
                    color: row.badge.tone === "beta" ? AMBER : TEAL,
                    border: `1px solid ${row.badge.tone === "beta" ? AMBER : TEAL}55`,
                    backgroundColor: `${row.badge.tone === "beta" ? AMBER : TEAL}1A`,
                  }}
                >
                  {row.badge.text[lang]}
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: "11px",
                lineHeight: 1.4,
                color: "rgba(255,255,255,0.35)",
                marginTop: "2px",
              }}
            >
              {row.desc[lang]}
            </div>
          </td>

          {/* Free */}
          <td
            className="text-center transition-colors group-hover:bg-white/[0.02]"
            style={{
              padding: "14px 8px",
              borderTop: "0.5px solid rgba(255,255,255,0.04)",
            }}
          >
            <ValueCell value={row.free} plan="free" lang={lang} />
          </td>

          {/* Pro : highlighted */}
          <td
            className="text-center"
            style={{
              padding: "14px 8px",
              backgroundColor: "rgba(0,201,167,0.04)",
              borderTop: "0.5px solid rgba(0,201,167,0.10)",
              borderLeft: `1.5px solid ${PRO_BORDER}`,
              borderRight: `1.5px solid ${PRO_BORDER}`,
            }}
          >
            <ValueCell value={row.pro} plan="pro" lang={lang} />
          </td>

          {/* Max */}
          <td
            className="text-center transition-colors group-hover:bg-white/[0.02]"
            style={{
              padding: "14px 8px",
              borderTop: "0.5px solid rgba(255,255,255,0.04)",
            }}
          >
            <ValueCell value={row.max} plan="max" lang={lang} />
          </td>
        </tr>
      ))}
    </>
  );
}

/* ── Plan header (name + price + CTA) ─────────────────────────────────────── */

function PlanHead({
  plan,
  name,
  price,
  period,
  badge,
  cta,
  onClick,
}: {
  plan: Plan;
  name: string;
  price: string;
  period?: string;
  badge?: string;
  cta: string;
  onClick: () => void;
}) {
  const nameColor =
    plan === "pro" ? TEAL : plan === "max" ? AMBER : "rgba(255,255,255,0.6)";

  return (
    <div className="flex flex-col items-center gap-2.5">
      {badge ? (
        <span
          className="font-semibold uppercase"
          style={{
            fontSize: "10px",
            letterSpacing: "0.04em",
            padding: "3px 11px",
            borderRadius: "999px",
            backgroundColor: TEAL,
            color: "#0F1729",
            boxShadow: "0 0 18px rgba(0,201,167,0.5)",
          }}
        >
          {badge}
        </span>
      ) : (
        <span style={{ height: "18px" }} aria-hidden="true" />
      )}

      <span
        className="uppercase"
        style={{
          fontSize: "13px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: nameColor,
        }}
      >
        {name}
      </span>

      <span
        className="font-display"
        style={{
          fontSize: plan === "free" ? "22px" : "28px",
          fontWeight: 800,
          color: "#fff",
          lineHeight: 1,
          letterSpacing: "-0.02em",
        }}
      >
        {price}
        {period && (
          <span
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "rgba(255,255,255,0.4)",
            }}
          >
            {period}
          </span>
        )}
      </span>

      <div className="mt-1 w-full">
        <PlanCta plan={plan} label={cta} onClick={onClick} />
      </div>
    </div>
  );
}

/* ── Plan CTA button (shared, theme by plan) ──────────────────────────────── */

function PlanCta({
  plan,
  label,
  onClick,
}: {
  plan: Plan;
  label: string;
  onClick: () => void;
}) {
  const base: React.CSSProperties = {
    width: "100%",
    maxWidth: "176px",
    margin: "0 auto",
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    padding: "9px 12px",
    borderRadius: "9px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "transform 150ms ease, box-shadow 200ms ease, background-color 150ms ease, border-color 150ms ease",
  };

  const themed: React.CSSProperties =
    plan === "pro"
      ? {
          ...base,
          backgroundColor: TEAL,
          color: "#0F1729",
          border: "none",
          boxShadow: "0 8px 24px -8px rgba(0,201,167,0.6)",
        }
      : plan === "max"
        ? {
            ...base,
            backgroundColor: "rgba(239,159,39,0.06)",
            color: AMBER,
            border: `1px solid rgba(239,159,39,0.4)`,
          }
        : {
            ...base,
            backgroundColor: "rgba(255,255,255,0.06)",
            color: "rgba(255,255,255,0.7)",
            border: "0.5px solid rgba(255,255,255,0.14)",
          };

  // Hover micro-interaction tuned per plan (no layout shift: transform + shadow).
  const onEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    el.style.transform = "translateY(-1px)";
    if (plan === "pro") {
      el.style.boxShadow = "0 12px 30px -8px rgba(0,201,167,0.75)";
    } else if (plan === "max") {
      el.style.backgroundColor = "rgba(239,159,39,0.12)";
      el.style.borderColor = "rgba(239,159,39,0.6)";
    } else {
      el.style.backgroundColor = "rgba(255,255,255,0.1)";
      el.style.borderColor = "rgba(255,255,255,0.22)";
    }
  };
  const onLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    el.style.transform = "translateY(0)";
    el.style.boxShadow = plan === "pro" ? "0 8px 24px -8px rgba(0,201,167,0.6)" : "none";
    if (plan === "max") {
      el.style.backgroundColor = "rgba(239,159,39,0.06)";
      el.style.borderColor = "rgba(239,159,39,0.4)";
    } else if (plan === "free") {
      el.style.backgroundColor = "rgba(255,255,255,0.06)";
      el.style.borderColor = "rgba(255,255,255,0.14)";
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      style={themed}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {label}
    </button>
  );
}
